import { React, useContext, useState, useEffect } from "react";

import ThemeContext from "../../lib/theme-context";
import AuthContext from "../../lib/auth-context";
import { DataFetcher } from "../../utils/dataFetcher";

import moment from "moment";
import dayjs from "dayjs";

import CustomSingleTextField from "../textfields/singleRow";
import CustomMultiTextField from "../textfields/multiRow";
import CustomDatePicker from "../dateTimePickers/datePicker";
import InstrumentSelect from "../select/InstrumentSelect";

import Modal from "@mui/material/Modal";
import Box from "@mui/material/Box";

import SaveIcon from "@mui/icons-material/Save";
import ClearAllIcon from "@mui/icons-material/ClearAll";
import CloseIcon from "@mui/icons-material/Close";
import useWindowDimensions from "../../utils/width";

const Backtests = ({
  open,
  onClose,
  setAllbackTestSchemas,
  handleArchivingTest,
  editMode = false,
  editBacktest = null,
}) => {
  const auth = useContext(AuthContext);
  const throwMessage = auth?.throwMessage;
  const theme = useContext(ThemeContext);
  const { width } = useWindowDimensions();
  const [backtestPair, setBacktestPair] = useState(editBacktest?.pair || "");
  const [startTest, setStartTest] = useState(
    editBacktest?.startDate ? dayjs(editBacktest.startDate) : dayjs(new Date()),
  );
  const [endTest, setEndTest] = useState(
    editBacktest?.exitDate ? dayjs(editBacktest.exitDate) : dayjs(new Date()),
  );
  const [notes, setNotes] = useState(editBacktest?.notes || "");
  const [testId, setTestId] = useState(editBacktest?.testId || "");
  const [originalTestId, setOriginalTestId] = useState(
    editBacktest?.testId || "",
  );

  const [formSubmitted, setFormSubmitted] = useState(false);

  // Disable body scroll when modal is open
  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }

    return () => {
      document.body.style.overflow = "unset";
    };
  }, [open]);

  useEffect(() => {
    if (editBacktest) {
      setBacktestPair(editBacktest.pair || "");
      setStartTest(
        editBacktest.startDate
          ? dayjs(editBacktest.startDate)
          : dayjs(new Date()),
      );
      setEndTest(
        editBacktest.exitDate
          ? dayjs(editBacktest.exitDate)
          : dayjs(new Date()),
      );
      setNotes(editBacktest.notes || "");
      setTestId(editBacktest.testId || "");
      setOriginalTestId(editBacktest.testId || "");
    } else {
      setBacktestPair("");
      setStartTest(dayjs(new Date()));
      setEndTest(dayjs(new Date()));
      setNotes("");
      setTestId("");
      setOriginalTestId("");
    }
  }, [editBacktest, open]);

  const submitNewTest = async (handleCloseBacktest) => {
    if (testId) {
      if (editMode) {
        if (testId !== originalTestId) {
          const duplicateBacktest = auth.backtests.find(
            (backtest) =>
              backtest.testId === testId && backtest._id !== editBacktest._id,
          );

          if (duplicateBacktest) {
            throwMessage("warning", "A backtest with this name already exists");
            return;
          }
        }

        if (!formSubmitted);
        setFormSubmitted(true);

        const updates = [
          { field: "testId", value: testId },
          { field: "pair", value: backtestPair },
          { field: "startDate", value: startTest },
          { field: "exitDate", value: endTest },
          { field: "notes", value: notes },
          {
            field: "duration",
            value: endTest
              ? moment(endTest.$d || endTest).diff(
                  moment(startTest.$d || startTest),
                  "weeks",
                )
              : 0,
          },
        ];

        let updatedBacktest = editBacktest;
        for (const update of updates) {
          const schema = {
            id: editBacktest._id,
            field: update.field,
            value: update.value,
          };
          const result = await DataFetcher("edit-backtest", schema);
          if (result && result._id) {
            updatedBacktest = result;
          } else {
            if (
              typeof result === "string" &&
              result.includes("already exists")
            ) {
              throwMessage("warning", result);
            } else {
              throwMessage("error", "Something went wrong");
            }
            setFormSubmitted(false);
            return;
          }
        }

        const filteredArray = auth.backtests.filter(
          (x) => x._id !== updatedBacktest._id,
        );
        filteredArray.push(updatedBacktest);
        setAllbackTestSchemas(filteredArray);
        auth.setBacktests(filteredArray);

        handleCloseBacktest();
        setFormSubmitted(false);
        throwMessage("success", "Backtest updated successfully");
      } else {
        const duplicateBacktest = auth.backtests.find(
          (backtest) => backtest.testId === testId,
        );

        if (duplicateBacktest) {
          throwMessage("warning", "A backtest with this name already exists");
          return;
        }

        if (!formSubmitted);
        setFormSubmitted(true);
        const schema = {
          testId: testId,
          pair: backtestPair,
          startDate: startTest,
          exitDate: endTest,
          notes: notes,
          duration: endTest
            ? moment(endTest.$d || endTest).diff(
                moment(startTest.$d || startTest),
                "weeks",
              )
            : 0,
        };
        const result = await DataFetcher("new-backtest", schema);
        if (result && result._id) {
          if (auth.backtests.length >= 3) {
            await handleArchivingTest({ _id: auth.backtests[2]._id });
            var foundIndex = auth.backtests.findIndex(
              (x) => x._id === auth.backtests[2]._id,
            );
            auth.backtests[foundIndex].archived = "true";
          }
          setAllbackTestSchemas([...auth.backtests, result]);
          auth.setBacktests([...auth.backtests, result]);

          setBacktestPair("");
          setStartTest(dayjs(new Date()));
          setEndTest(dayjs(new Date()));
          setNotes("");
          setTestId("");
          handleCloseBacktest();
          setFormSubmitted(false);
          throwMessage("success", "Success");
        } else {
          if (typeof result === "string" && result.includes("already exists")) {
            throwMessage("warning", result);
          } else {
            throwMessage("error", "Something went wrong");
          }
          setFormSubmitted(false);
        }
      }
    } else {
      throwMessage("warning", "Missing Fields");
    }
  };

  const clearForm = () => {
    setBacktestPair("");
    setNotes("");
    setTestId("");
    setStartTest(dayjs(new Date()));
    setEndTest(dayjs(new Date()));
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      disablePortal
      style={{ zIndex: 10000 }}
    >
      <Box
        sx={{
          position: "absolute",
          top: width <= 800 ? "0" : "50%",
          left: width <= 800 ? "0" : "50%",
          transform: width <= 800 ? "none" : "translate(-50%, -50%)",
          width: width <= 800 ? "100%" : "auto",
          maxWidth: width <= 800 ? "100%" : "1200px",
          maxHeight: width <= 800 ? "100vh" : "90vh",
          backgroundColor: theme[0] === "dark" ? "#1a1b1e" : "#ffffff",
          borderRadius: width <= 800 ? "0px" : "12px",
          border: width <= 800 ? "none" : (theme[0] === "dark" ? "1px solid #2c2d31" : "1px solid #e5e7eb"),
          boxShadow:
            theme[0] === "dark"
              ? "0 25px 50px -12px rgba(0, 0, 0, 0.5), 0 0 0 1px rgba(255, 255, 255, 0.05)"
              : "0 25px 50px -12px rgba(0, 0, 0, 0.15), 0 0 0 1px rgba(0, 0, 0, 0.05)",
          p: width <= 800 ? 2 : 4,
          pt: width <= 800 ? 4 : 4,
          pb: width <= 800 ? 8 : 4,
          outline: "none",
        }}
      >
        <div
          style={{
            backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
            borderRadius: "12px",
            maxHeight: "calc(90vh - 32px)",
            overflow: "auto",
            position: "relative",
          }}
        >
          <h2
            style={{
              textAlign: "center",
              fontWeight: "600",
              fontSize: "24px",
              margin: "0.5em 0 1em 0",
               padding: "0.5em 0",
              letterSpacing: "-0.5px",
              color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
            }}
          >
            {editMode ? "Edit Backtest" : (testId || "New Backtest")}{backtestPair && ` : ${backtestPair}`}
          </h2>
          {width <= 800 && (
          <button
            onClick={onClose}
            style={{
              position: "absolute",
              right: "16px",
              top: "50%",
              transform: "translateY(-50%)",
              backgroundColor: "transparent",
              border: "none",
              color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
              cursor: "pointer",
              padding: "8px",
              borderRadius: "8px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              transition: "all 0.2s ease",
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.backgroundColor =
                theme[0] === "dark"
                  ? "rgba(156, 163, 175, 0.1)"
                  : "rgba(107, 114, 128, 0.1)";
              e.currentTarget.style.color =
                theme[0] === "dark" ? "#d1d5db" : "#4b5563";
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
              e.currentTarget.style.color =
                theme[0] === "dark" ? "#9ca3af" : "#6b7280";
            }}
          >
            <CloseIcon sx={{ fontSize: "20px" }} />
          </button>
          )}
        </div>

        <div
          style={{
            backgroundColor: theme[0] === "dark" ? "#25262b" : "#f9fafb",
            border: theme[0] === "dark" ? "1px solid #2c2d31" : "1px solid #e5e7eb",
            borderRadius: "12px",
            padding: "20px",
            marginTop: "1.6em",
          }}
        >
          <div style={{ display: "grid", gridTemplateColumns: width <= 900 ? "1fr" : "repeat(12, 1fr)", gap: "24px" }}>
          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 6" }}>
            <span
              style={{
                fontSize: "11px",
                fontWeight: "500",
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                display: "block",
                marginBottom: "4px",
                visibility: "hidden",
              }}
            >
              Instrument
            </span>
            <InstrumentSelect
              value={backtestPair}
              onChange={(event) => {
                setBacktestPair(event.target.value);
              }}
              size="small"
              fullWidth
            />
          </div>

          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 6" }}>
            <span
              style={{
                fontSize: "11px",
                fontWeight: "500",
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                display: "block",
                marginBottom: "4px",
              }}
            >
              Test Name
            </span>
            <CustomSingleTextField
              size="small"
              value={testId}
              onChange={(e) => setTestId(e.target.value)}
            />
          </div>

          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 5" }}>
            <CustomDatePicker
              size="small"
              label="Start Date"
              value={startTest}
              onChange={(newValue) => setStartTest(newValue)}
            />
          </div>
          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 2", display: "flex", alignItems: "center", justifyContent: "center" }}>
            <div
              style={{
                fontSize: "14px",
                textAlign: "center",
                color: theme[0] === "dark" ? "#FCFCFF" : "#1a1b1e",
                fontWeight: "500",
                padding: "8px",
                backgroundColor: theme[0] === "dark" ? "#2c2d31" : "#f3f4f6",
                borderRadius: "8px",
                border:
                  theme[0] === "dark"
                    ? "1px solid #3a3b40"
                    : "1px solid #e5e7eb",
                height: "40px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              {(() => {
                // Convert dayjs objects to JavaScript Date objects
                const startDate = startTest.$d || startTest;
                const endDate = endTest.$d || endTest;

                const startMoment = moment(startDate);
                const endMoment = moment(endDate);

                const weeks = endMoment.diff(startMoment, "weeks");
                const days = endMoment.diff(startMoment, "days");

                if (weeks === 0) {
                  return `${days} ${days === 1 ? "Day" : "Days"}`;
                }
                return `${weeks} ${weeks === 1 ? "Week" : "Weeks"}`;
              })()}
            </div>
          </div>
          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 5" }}>
            <CustomDatePicker
              size="small"
              label="End Date"
              value={endTest}
              onChange={(newValue) => setEndTest(newValue)}
            />
          </div>

          <div style={{ gridColumn: width <= 900 ? "span 1" : "span 12" }}>
            <span
              style={{
                fontSize: "11px",
                fontWeight: "500",
                color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
                display: "block",
                marginBottom: "4px",
              }}
            >
              Notes
            </span>
            <CustomMultiTextField
              size="small"
              rows={6}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
        </div>
        </div>

        <div style={{ paddingTop: "34px" }}>
          <div style={{ display: "grid", gridTemplateColumns: width <= 500 ? "repeat(2, 1fr)" : "repeat(2, 1fr)", gap: width <= 500 ? "8px" : "16px" }}>
            <div>
              <button
                onClick={() => clearForm()}
                style={{
                  width: "100%",
                  padding: width <= 500 ? "12px 8px" : "12px 16px",
                  backgroundColor:
                    theme[0] === "dark"
                      ? "rgba(211, 78, 78, 0.15)"
                      : "rgba(211, 78, 78, 0.1)",
                  border:
                    theme[0] === "dark"
                      ? "1px solid rgba(211, 78, 78, 0.4)"
                      : "1px solid rgba(211, 78, 78, 0.3)",
                  borderRadius: "8px",
                  color: "#D34E4E",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(211, 78, 78, 0.25)"
                      : "rgba(211, 78, 78, 0.15)";
                }}
                onMouseOut={(e) => {
                  e.currentTarget.style.backgroundColor =
                    theme[0] === "dark"
                      ? "rgba(211, 78, 78, 0.15)"
                      : "rgba(211, 78, 78, 0.1)";
                }}
              >
                <ClearAllIcon fontSize="small" />
                {width > 500 && "Clear"}
              </button>
            </div>
            <div>
              <button
                onClick={() => submitNewTest(onClose)}
                disabled={formSubmitted}
                style={{
                  width: "100%",
                  padding: width <= 500 ? "12px 8px" : "12px 16px",
                  backgroundColor: formSubmitted
                    ? theme[0] === "dark"
                      ? "#2d2e33"
                      : "#e5e7eb"
                    : theme[0] === "dark"
                      ? "rgba(38, 166, 93, 0.15)"
                      : "rgba(38, 166, 93, 0.1)",
                  border: formSubmitted
                    ? theme[0] === "dark"
                      ? "1px solid #3a3b3e"
                      : "1px solid #d1d5db"
                    : theme[0] === "dark"
                      ? "1px solid rgba(38, 166, 93, 0.4)"
                      : "1px solid rgba(38, 166, 93, 0.3)",
                  borderRadius: "8px",
                  color: formSubmitted
                    ? theme[0] === "dark"
                      ? "#6b7280"
                      : "#9ca3af"
                    : "#26a65d",
                  fontSize: "14px",
                  fontWeight: "600",
                  cursor: formSubmitted ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: "8px",
                  transition: "all 0.2s ease",
                }}
                onMouseOver={(e) => {
                  if (!formSubmitted) {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.25)"
                        : "rgba(38, 166, 93, 0.15)";
                  }
                }}
                onMouseOut={(e) => {
                  if (!formSubmitted) {
                    e.currentTarget.style.backgroundColor =
                      theme[0] === "dark"
                        ? "rgba(38, 166, 93, 0.15)"
                        : "rgba(38, 166, 93, 0.1)";
                  }
                }}
              >
                <SaveIcon fontSize="small" />
                {width > 500 && "Submit"}
              </button>
            </div>
          </div>
        </div>
      </Box>
    </Modal>
  );
};

export default Backtests;
